import {
  TableContainer,
  Table,
  TableHead,
  TableRow as MuiTableRow,
  TableCell as MuiTableCell,
  TableBody,
  TableCellProps,
  styled,
  TableRowProps,
} from '@mui/material';
import React, { useCallback, useMemo } from 'react';
import {
  BillChargeType, TableColumnIdEnum, PlanColumns, SeasonEnum,
} from '../../constants';
import {
  BillEstimateResponse, BillLineItem, EstimationComparisonTableRow, RangeUsageKWh,
} from '../../models';
import { estimationComparisonTableColumns } from './BillChargeTableColumns';

const TableCell = styled(MuiTableCell)<TableCellProps>`
  &.MuiTableCell-root {
    border: 0.5px solid #eee;
  }
`;

const TableRow = styled(MuiTableRow)<TableRowProps>`
  &:nth-of-type(odd) {
    background-color: #f2f2f2;
  }
`;

const TotalTableRow = styled(MuiTableRow)<TableRowProps>`
  background-color: #d9d9d9;
`;

const getBillItemDefaultValue = (date: string) => ({
  date: date?.toString() || '',
  month: '',
  year: '',
  billTotal: 0,
  description: '',
  chargeType: '',
  charge: 0,
  quantity: 0,
  unit: '',
  timeQuantity: 0,
  timeUnit: '',
  rate: 0,
  isMissingMonth: true,
}) as BillLineItem;

function BillChargeTable(props: BillChargeTableProps) {
  const {
    customerRatePlan, billLineItems, billEstimation, monthlyUsages,
  } = props;

  const fixMissingMonths = useCallback(() => {
    const monthsFromBillLineItems = billLineItems.map((item) => item[1]?.[0]?.month);
    const missingMonthData = monthlyUsages.find((month) => !monthsFromBillLineItems.includes(month.month));
    if (missingMonthData) {
      const defaultBillLineItemData = getBillItemDefaultValue(missingMonthData?.fromDate);
      const isMonthExist = billLineItems.map((item) => item[0]).some((dt) => dt === defaultBillLineItemData.date?.toString());
      if (!isMonthExist) {
        billLineItems.push([defaultBillLineItemData.date?.toString() || '', [defaultBillLineItemData]]);
        billLineItems.sort((a, b) => new Date(a[0]).getMonth() - new Date(b[0]).getMonth());
      }
    }
  }, [billLineItems, monthlyUsages]);

  const planColumnIds = useMemo(() => PlanColumns[customerRatePlan] || [], [customerRatePlan]);
  const tableData = useMemo<EstimationComparisonTableRow[]>(
    () => {
      fixMissingMonths();
      return billLineItems?.map((x) => {
        const lineItems = x[1];
        const charge = billEstimation?.charges.find((y) => new Date(y.fromDate).toISOString() === new Date(x[0]).toISOString());
        const usages = monthlyUsages.filter((y) => new Date(y.fromDate).toISOString() === new Date(x[0]).toISOString());

        const extractValue = (chargeTypes: string[], valueSelector: (item: BillLineItem | undefined) => number | undefined) => {
          const allItems = lineItems.filter((y) => chargeTypes.some((z) => z.toLowerCase() === y.chargeType?.toLowerCase()));
          const uniqueChargeTypes = new Set<string>();
          const sumOfObjectsValue = allItems.filter((y) => {
            const chargeType = y.chargeType ?? '';
            if (valueSelector(y) !== 0 && !uniqueChargeTypes.has(chargeType)) {
              uniqueChargeTypes.add(chargeType);
              return true;
            }
            return false;
          }).reduce((acc, cur) => ({
            billTotal: acc.billTotal,
            charge: acc.charge + (cur.charge ?? 0),
            quantity: acc.quantity + (cur.quantity ?? 0),
            rate: acc.rate + (cur.rate ?? 0),
            timeQuantity: acc.timeQuantity + (cur.timeQuantity ?? 0),
          }), {
            billTotal: 0,
            charge: 0,
            quantity: 0,
            rate: 0,
            timeQuantity: 0,
          });
          const updateQuantity = allItems.map((val) => ({ ...val, ...sumOfObjectsValue }));
          const item = updateQuantity.find((y) => valueSelector(y) !== 0);
          return valueSelector(item) || 0;
        };

        const extractStepCharge = (step: 1 | 2 | 3, season: SeasonEnum) => {
          const stepCharge = charge?.stepCharges?.find((y) => y.step === step && y.season === season);
          return stepCharge?.stepCharge || 0;
        };

        const fromDate = new Date(charge?.fromDate || '');
        const toDate = new Date(charge?.toDate || '');

        const row = {
          month: charge?.month,
          fromDate: new Date(fromDate.setDate(fromDate.getDate() + 1)),
          toDate: new Date(toDate.setDate(toDate.getDate() + 1)),
          billedDays: charge?.billedDays || 0,
          onPeakCharge: charge?.onPeak || 0,
          midPeakCharge: charge?.midPeak || 0,
          offPeakCharge: charge?.offPeak || 0,
          totalEnergyCharge: charge?.totalEnergy || 0,
          totalServiceCharge: charge?.totalServiceCharges || 0,
          onPeakDemandCharge: charge?.onPeakDemand || 0,
          customerDemandCharge: charge?.customerDemand || 0,
          fuelAdjustmentCharge: charge?.fuelAdjustmentCharge || 0,
          winter1StepCharge: extractStepCharge(1, SeasonEnum.Winter),
          winter2StepCharge: extractStepCharge(2, SeasonEnum.Winter),
          winter3StepCharge: extractStepCharge(3, SeasonEnum.Winter),
          summer1StepCharge: extractStepCharge(1, SeasonEnum.Summer),
          summer2StepCharge: extractStepCharge(2, SeasonEnum.Summer),
          summer3StepCharge: extractStepCharge(3, SeasonEnum.Summer),
          totalCharge: (charge?.total || 0) - (charge?.totalFuelAdjustmentCharge || 0),
          onPeakUsage: usages.map((y) => y.onPeak).reduce((a, b) => a + b, 0),
          midPeakUsage: usages.map((y) => y.midPeak).reduce((a, b) => a + b, 0),
          offPeakUsage: usages.map((y) => y.offPeak).reduce((a, b) => a + b, 0),

          billOnPeakUsage: extractValue(BillChargeType.onPeakUsage, (y) => y?.quantity),
          billMidPeakUsage: extractValue(BillChargeType.midPeakUsage, (y) => y?.quantity),
          billOffPeakUsage: extractValue(BillChargeType.offPeakUsage, (y) => y?.quantity),
          billTotalUsage:
            extractValue(BillChargeType.onPeakUsage, (y) => y?.quantity)
            + extractValue(BillChargeType.midPeakUsage, (y) => y?.quantity)
            + extractValue(BillChargeType.offPeakUsage, (y) => y?.quantity),
          billOnPeakCharge: extractValue(BillChargeType.onPeakCharge, (y) => y?.charge),
          billMidPeakCharge: extractValue(BillChargeType.midPeakCharge, (y) => y?.charge),
          billOffPeakCharge: extractValue(BillChargeType.offPeakCharge, (y) => y?.charge),
          billWinter1StepCharge: extractValue(BillChargeType.winter1Step, (y) => y?.charge),
          billWinter2StepCharge: extractValue(BillChargeType.winter2Step, (y) => y?.charge),
          billWinter3StepCharge: extractValue(BillChargeType.winter3Step, (y) => y?.charge),
          billSummer1StepCharge: extractValue(BillChargeType.summer1Step, (y) => y?.charge),
          billSummer2StepCharge: extractValue(BillChargeType.summer2Step, (y) => y?.charge),
          billSummer3StepCharge: extractValue(BillChargeType.summer3Step, (y) => y?.charge),
          billTotalEnergyCharge: extractValue(BillChargeType.onPeakCharge, (y) => y?.charge)
            + extractValue(BillChargeType.midPeakCharge, (y) => y?.charge)
            + extractValue(BillChargeType.offPeakCharge, (y) => y?.charge)
            + extractStepCharge(1, SeasonEnum.Winter)
            + extractStepCharge(2, SeasonEnum.Winter)
            + extractStepCharge(3, SeasonEnum.Winter)
            + extractStepCharge(1, SeasonEnum.Summer)
            + extractStepCharge(2, SeasonEnum.Summer)
            + extractStepCharge(3, SeasonEnum.Summer),
          billTotalServiceCharge: extractValue(BillChargeType.serviceCharge, (y) => y?.charge),
          billOnPeakDemandCharge: extractValue(BillChargeType.onPeakDemandCharge, (y) => y?.charge),
          billCustomerDemandCharge: extractValue(BillChargeType.customerDemandCharge, (y) => y?.charge),
          billTotalCharge:
            extractValue(BillChargeType.onPeakCharge, (y) => y?.charge)
            + extractValue(BillChargeType.midPeakCharge, (y) => y?.charge)
            + extractValue(BillChargeType.offPeakCharge, (y) => y?.charge)
            + extractStepCharge(1, SeasonEnum.Winter)
            + extractStepCharge(2, SeasonEnum.Winter)
            + extractStepCharge(3, SeasonEnum.Winter)
            + extractStepCharge(1, SeasonEnum.Summer)
            + extractStepCharge(2, SeasonEnum.Summer)
            + extractStepCharge(3, SeasonEnum.Summer)
            + extractValue(BillChargeType.serviceCharge, (y) => y?.charge)
            + extractValue(BillChargeType.onPeakDemandCharge, (y) => y?.charge)
            + extractValue(BillChargeType.customerDemandCharge, (y) => y?.charge),
          billTotal: lineItems?.[0]?.billTotal || 0,
          isMissingMonth: lineItems?.[0]?.isMissingMonth || false,
        };

        const calculatedRow = {
          ...row,
          totalUsage: row.onPeakUsage + row.midPeakUsage + row.offPeakUsage,
          totalDiff: row.totalCharge - row.billTotalCharge,
          totalDiffPercentage: ((row.totalCharge - row.billTotalCharge) / row.totalCharge) * 100,
          ignored: row.billTotal - row.billTotalCharge,
          ignoredPercentage: ((row.billTotal - row.billTotalCharge) / row.billTotal) * 100,
        };

        return calculatedRow;
      });
    },
    [billLineItems, billEstimation?.charges, monthlyUsages, fixMissingMonths],
  );

  const totalRow = useMemo(() => {
    // eslint-disable-next-line max-len
    const sum = (keySelector: (x: EstimationComparisonTableRow) => number) => tableData?.filter((x) => !x.isMissingMonth).map(keySelector).reduce((a, b) => a + b, 0);
    return {
      billedDays: sum((x) => x.billedDays),
      onPeakUsage: sum((x) => x.onPeakUsage),
      midPeakUsage: sum((x) => x.midPeakUsage),
      offPeakUsage: sum((x) => x.offPeakUsage),
      totalUsage: sum((x) => x.totalUsage),
      billOnPeakUsage: sum((x) => x.billOnPeakUsage),
      billMidPeakUsage: sum((x) => x.billMidPeakUsage),
      billOffPeakUsage: sum((x) => x.billOffPeakUsage),
      billTotalUsage: sum((x) => x.billTotalUsage),
      onPeakCharge: sum((x) => x.onPeakCharge),
      billOnPeakCharge: sum((x) => x.billOnPeakCharge),
      midPeakCharge: sum((x) => x.midPeakCharge),
      billMidPeakCharge: sum((x) => x.billMidPeakCharge),
      offPeakCharge: sum((x) => x.offPeakCharge),
      billOffPeakCharge: sum((x) => x.billOffPeakCharge),
      winter1StepCharge: sum((x) => x.winter1StepCharge),
      winter2StepCharge: sum((x) => x.winter2StepCharge),
      winter3StepCharge: sum((x) => x.winter3StepCharge),
      summer1StepCharge: sum((x) => x.summer1StepCharge),
      summer2StepCharge: sum((x) => x.summer2StepCharge),
      summer3StepCharge: sum((x) => x.summer3StepCharge),
      billWinter1StepCharge: sum((x) => x.billWinter1StepCharge),
      billWinter2StepCharge: sum((x) => x.billWinter2StepCharge),
      billWinter3StepCharge: sum((x) => x.billWinter3StepCharge),
      billSummer1StepCharge: sum((x) => x.billSummer1StepCharge),
      billSummer2StepCharge: sum((x) => x.billSummer2StepCharge),
      billSummer3StepCharge: sum((x) => x.billSummer3StepCharge),
      billTotalEnergyCharge: sum((x) => x.billTotalEnergyCharge),
      totalEnergyCharge: sum((x) => x.totalEnergyCharge),
      billTotalServiceCharge: sum((x) => x.billTotalServiceCharge),
      totalServiceCharge: sum((x) => x.totalServiceCharge),
      billOnPeakDemandCharge: sum((x) => x.billOnPeakDemandCharge),
      billCustomerDemandCharge: sum((x) => x.billCustomerDemandCharge),
      onPeakDemandCharge: sum((x) => x.onPeakDemandCharge),
      customerDemandCharge: sum((x) => x.customerDemandCharge),
      fuelAdjustmentCharge: sum((x) => x.fuelAdjustmentCharge),
      totalCharge: sum((x) => x.totalCharge),
      billTotalCharge: sum((x) => x.billTotalCharge),
      totalDiff: sum((x) => x.totalDiff),
      totalDiffPercentage: (sum((x) => x.totalDiff) / sum((x) => x.billTotalCharge)) * 100,
      billTotal: sum((x) => x.billTotal),
      ignored: sum((x) => x.ignored),
      ignoredPercentage: (sum((x) => x.ignored) / sum((x) => x.billTotal)) * 100,
    } as EstimationComparisonTableRow;
  }, [tableData]);

  const shouldDisplayColumn = useCallback(
    (columnId: TableColumnIdEnum) => (planColumnIds.includes(columnId) ? 'table-cell' : 'none'),
    [planColumnIds],
  );

  return (
    <TableContainer data-testid="estimation-comparison-table" sx={{ maxWidth: 1400 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow role="table-header-row">
            {
              estimationComparisonTableColumns.header.map((y) => (
                <TableCell
                  align={y.align}
                  colSpan={y.colSpan}
                  key={Math.random()}
                  sx={{ display: shouldDisplayColumn(y.columnId) }}
                >
                  {typeof y.label === 'string' && y.label}
                </TableCell>
              ))
            }
          </TableRow>
          <TableRow role="table-header-row">
            {
              estimationComparisonTableColumns.subHeader.map((y) => (
                <TableCell key={Math.random()} align={y.align} colSpan={y.colSpan} sx={{ display: shouldDisplayColumn(y.columnId) }}>
                  {typeof y.label === 'string' && y.label}
                </TableCell>
              ))
            }
          </TableRow>
        </TableHead>
        <TableBody>
          {tableData.map((data) => (
            <TableRow role="table-body-row" key={data.month}>
              {
                estimationComparisonTableColumns.body.map((y) => (
                  <TableCell
                    align={y.align}
                    colSpan={y.colSpan}
                    key={Math.random()}
                    sx={{ display: shouldDisplayColumn(y.columnId) }}
                  >
                    {typeof y.label === 'string' ? y.label : y.label?.(data)}
                  </TableCell>
                ))
              }
            </TableRow>
          ))}
          <TotalTableRow role="table-body-row" key="total">
            {
              estimationComparisonTableColumns.total.map((y, index) => (
                <TableCell
                  align={y.align}
                  colSpan={y.colSpan}
                  key={Math.random()}
                  sx={{ display: shouldDisplayColumn(y.columnId) }}
                >
                  {typeof y.label === 'string' ? y.label : y.label?.(totalRow)}
                </TableCell>
              ))
            }
          </TotalTableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
}

interface BillChargeTableProps {
  customerRatePlan: string;
  billLineItems: [string, BillLineItem[]][];
  billEstimation: BillEstimateResponse | undefined;
  monthlyUsages: RangeUsageKWh[];
}

export { BillChargeTable };
export type { BillChargeTableProps };
